/**
 * HomePuzzleRo from GET /home/puzzle (list/detail).
 * Root `id` is often template id; play routes use instance ids:
 * - New shape: `crossword.id` + `crossword.crossTpl`, `grid.id` + `grid.gridTpl`
 * - Legacy: `crossTpl.crossword.id`, `gridTpl.grid.id`; or flat `crossTpl` / `gridTpl`
 */

import { normalizeStoryRoTags as normalizeHomePuzzleRoTags } from "../browse-stories/storyRoUtils";

export { normalizeHomePuzzleRoTags };

function homeCrossTpl(ro) {
    const nested = ro?.crossword?.crossTpl ?? ro?.crossTpl;
    if (nested) return nested;
    const puzzleType = String(ro?.type ?? ro?.puzzleType ?? ro?.template?.type ?? "").toUpperCase();
    if (puzzleType !== "CROSSWORD") return null;
    return ro?.template ?? ro;
}

function homeGridTpl(ro) {
    const nested = ro?.grid?.gridTpl ?? ro?.gridTpl;
    if (nested) return Array.isArray(nested) ? nested[0] : nested;
    const puzzleType = String(ro?.type ?? ro?.puzzleType ?? ro?.template?.type ?? "").toUpperCase();
    if (puzzleType !== "WORD_SEARCH" && puzzleType !== "HIDDEN_MESSAGE") return null;
    return ro?.template ?? ro;
}

function resolvedPuzzleType(ro) {
    const ct = homeCrossTpl(ro);
    const gt = homeGridTpl(ro);
    return String(ro?.puzzleType ?? ro?.type ?? ct?.type ?? gt?.type ?? "").toUpperCase();
}

function nonEmptyTrimmed(v) {
    if (v == null) return null;
    const s = String(v).trim();
    return s !== "" ? s : null;
}

/** `/puzzle/play-cross/:id` — crossword **instance** id, not cross-tpl id. */
export function getHomeBrowseCrossPlayId(ro) {
    return (
        nonEmptyTrimmed(ro?.crossword?.id)
        ?? nonEmptyTrimmed(ro?.crossTpl?.crossword?.id)
        ?? nonEmptyTrimmed(ro?.crossTpl?.crossword_id)
        ?? nonEmptyTrimmed(ro?.crosswordId)
    );
}

/** `/puzzle/play-hidden-message/:id` or `/puzzle/play-word-search/:id` — grid **instance** id, not grid-tpl id. */
export function getHomeBrowseGridPlayId(ro) {
    return (
        nonEmptyTrimmed(ro?.grid?.id)
        ?? nonEmptyTrimmed(ro?.grid?.grid?.id)
        ?? nonEmptyTrimmed(ro?.gridTpl?.grid?.id)
        ?? nonEmptyTrimmed(ro?.gridTpl?.grid_id)
        ?? nonEmptyTrimmed(ro?.gridId)
    );
}

/** Puzzle display title from nested cross/grid tpl (GET /home/puzzle). */
export function getHomeBrowsePuzzleTitle(ro) {
    const ct = homeCrossTpl(ro);
    const gt = homeGridTpl(ro);
    const crossT = typeof ct?.title === "string" ? ct.title.trim() : "";
    const gridT = typeof gt?.title === "string" ? gt.title.trim() : "";
    if (crossT) return crossT;
    if (gridT) return gridT;
    const legacy = typeof ro?.templateTitle === "string" ? ro.templateTitle.trim() : "";
    return legacy || "—";
}

/** Tags for cards: cross/grid tpl or root tags only (`topic.tags` ignored; Bedrock stripped). */
export function getHomeBrowsePuzzleTags(ro) {
    const ct = homeCrossTpl(ro);
    const gt = homeGridTpl(ro);
    return normalizeHomePuzzleRoTags(ct?.tags ?? gt?.tags ?? ro?.tags);
}

/** Illustration for browse cards: icon URL preferred; fall back to full story image URLs from API/how-to fixtures. */
function homeBrowseStoryTeaserImageUrl(story) {
    if (!story || typeof story !== "object") return "";
    const u =
        story.imageIcon400Url
        ?? story.image_icon_400_url
        ?? story.imageUrl
        ?? story.image_url
        ?? story.imagePreviewUrl
        ?? story.image_preview_url;
    return typeof u === "string" ? u.trim() : "";
}

/** Same resolution as teaser: row, crossword/grid wrappers, tpl. */
function homeBrowseStoryObject(ro) {
    const ct = homeCrossTpl(ro);
    const gt = homeGridTpl(ro);
    return (
        ro?.story
        ?? ro?.crossword?.story
        ?? ro?.grid?.story
        ?? ct?.story
        ?? gt?.story
        ?? ro?.template?.story
        ?? null
    );
}

/** Story id on puzzle row when a narrative exists (legacy `/stories/:id` removed). */
export function getHomeBrowseStoryId(ro) {
    const story = homeBrowseStoryObject(ro);
    if (!story || typeof story !== "object") return null;
    return nonEmptyTrimmed(story.id);
}

/** Id for `GET /home/story/:id` from a carousel / browse teaser row (story id, else root/template id). */
export function getHomeStoryReadApiId(ro) {
    const fromStory = getHomeBrowseStoryId(ro);
    if (fromStory) return fromStory;
    const root = nonEmptyTrimmed(ro?.id);
    if (root) return root;
    const ct = homeCrossTpl(ro);
    const gt = homeGridTpl(ro);
    return nonEmptyTrimmed(ct?.id) ?? nonEmptyTrimmed(gt?.id);
}

/** Published sliding/jigsaw links from `story.sliding` / `story.jigsaw` on home browse puzzle rows. */
export function getHomeBrowseStoryLinkedPuzzleLinks(ro) {
    const story = homeBrowseStoryObject(ro);
    const storyId = getHomeStoryReadApiId(ro);
    if (!storyId) {
        return { slidingPlayId: null, jigsawPlayId: null };
    }
    const sliding = story?.sliding;
    const jigsaw = story?.jigsaw;
    return {
        slidingPlayId: sliding?.state === "PUBLISHED" ? storyId : null,
        jigsawPlayId: jigsaw?.state === "PUBLISHED" ? storyId : null,
    };
}

/**
 * Story object: `crossword.story` / `grid.story` (home/v2 puzzle), row, crossTpl, or gridTpl.
 * Shape: `{ id?, en: { title, text }, imageIcon400Url }` (or `stories.en`; teaser image may use `imageUrl` / `imagePreviewUrl`).
 */
export function getHomeBrowseStoryTeaser(ro) {
    const story = homeBrowseStoryObject(ro);
    if (!story || typeof story !== "object") {
        return { storyTitle: "", imageUrl: "" };
    }
    const en = story.en ?? story.stories?.en ?? {};
    const storyTitle = typeof en?.title === "string" ? en.title.trim() : "";
    const imageUrl = homeBrowseStoryTeaserImageUrl(story);
    return { storyTitle, imageUrl };
}

/**
 * Story title for browse cards using content language (`english` / `french`);
 * illustration URL prefers `imageIcon400Url`, then `imageUrl` / `imagePreviewUrl` (same for all locales).
 */
export function getLocalizedHomeBrowseStoryTeaser(ro, contentLanguage) {
    const story = homeBrowseStoryObject(ro);
    if (!story || typeof story !== "object") {
        return { storyTitle: "", imageUrl: "" };
    }
    const lang = String(contentLanguage || "english").toLowerCase();
    const useFr = lang === "french" || lang === "fr";
    const blob = useFr
        ? (story.fr ?? story.stories?.fr ?? story.en ?? story.stories?.en ?? {})
        : (story.en ?? story.stories?.en ?? story.fr ?? story.stories?.fr ?? {});
    const storyTitle = typeof blob?.title === "string" ? blob.title.trim() : "";
    const imageUrl = homeBrowseStoryTeaserImageUrl(story);
    return { storyTitle, imageUrl };
}

/**
 * Browse UI: one type chip — "Crossword" OR "Word Search" OR "Hidden Message" (never duplicate
 * Crossword, never show "Grid" alone).
 * @returns {"CROSSWORD"|"WORD_SEARCH"|"HIDDEN_MESSAGE"|null}
 */
export function getBrowsePuzzleDisplayCategory(ro) {
    const kind = String(ro?.kind ?? "").toUpperCase();
    const puzzleType = resolvedPuzzleType(ro);

    if (kind === "CROSSWORD" || puzzleType === "CROSSWORD") {
        return "CROSSWORD";
    }
    if (puzzleType === "WORD_SEARCH") return "WORD_SEARCH";
    if (puzzleType === "HIDDEN_MESSAGE") return "HIDDEN_MESSAGE";
    return null;
}

/** Play path: uses crossword/grid **instance** ids (matches Word-of-day). */
export function getHomePuzzlePlayPath(ro) {
    const kind = String(ro?.kind ?? "").toUpperCase();
    const puzzleType = resolvedPuzzleType(ro);

    if (kind === "CROSSWORD" || puzzleType === "CROSSWORD") {
        const crossId = getHomeBrowseCrossPlayId(ro);
        if (!crossId) return null;
        return `/puzzle/play-cross/${encodeURIComponent(crossId)}`;
    }

    const gridKind =
        kind === "GRID" || puzzleType === "WORD_SEARCH" || puzzleType === "HIDDEN_MESSAGE";
    if (gridKind) {
        const gridId = getHomeBrowseGridPlayId(ro);
        if (!gridId) return null;
        const enc = encodeURIComponent(gridId);
        const gt = homeGridTpl(ro);
        const gridType = puzzleType || String(gt?.type ?? "").toUpperCase();
        if (gridType === "HIDDEN_MESSAGE") return `/puzzle/play-hidden-message/${enc}`;
        if (gridType === "WORD_SEARCH") return `/puzzle/play-word-search/${enc}`;
        if (kind === "GRID") {
            const t = String(gt?.type ?? "WORD_SEARCH").toUpperCase();
            if (t === "HIDDEN_MESSAGE") return `/puzzle/play-hidden-message/${enc}`;
            return `/puzzle/play-word-search/${enc}`;
        }
    }

    return null;
}

/** Browse list: only show templates that are published for players. */
export function isHomeBrowsePuzzlePublished(ro) {
    const ct = ro?.crossword?.crossTpl ?? ro?.crossTpl;
    const gt = ro?.grid?.gridTpl ?? ro?.gridTpl;
    const tpl = ct || gt;
    if (!tpl || typeof tpl !== "object") return false;
    return String(tpl.state ?? "").toUpperCase() === "PUBLISHED";
}

function tplWordsInUseForCard(tpl) {
    if (!tpl || typeof tpl.wordsInUse !== "string") return "";
    return tpl.wordsInUse;
}

/** Shape for CrossCard; `id` is play instance id. */
export function homeBrowseRowToCrossCardPuzzle(ro) {
    const ct = ro?.crossword?.crossTpl ?? ro?.crossTpl;
    if (!ct || String(ct.type ?? "").toUpperCase() !== "CROSSWORD") return null;
    const playId = getHomeBrowseCrossPlayId(ro);
    if (!playId) return null;
    const title =
        typeof ct.title === "string" && ct.title.trim() ? ct.title.trim() : getHomeBrowsePuzzleTitle(ro);
    return {
        id: playId,
        type: "CROSSWORD",
        title,
        difficultyLevel: ct.difficultyLevel ?? ro?.topic?.difficultyLevel ?? null,
        publishedAt: ct.publishedAt ?? null,
        free: ct.free === true,
        words: tplWordsInUseForCard(ct),
        template: {
            clusteredBubbles: ct.clusteredBubbles ?? null,
            tags: ct.tags,
            topic: ct.topic,
        },
        genre: ro?.topic?.genre ?? null,
        topic: ro?.topic,
        state: ct.state,
        disabled: ct.state === "DISABLED" || ct.disabled === true,
    };
}

/** Shape for GridCard; `id` is play instance id. */
export function homeBrowseRowToGridCardPuzzle(ro) {
    const gt = ro?.grid?.gridTpl ?? ro?.gridTpl;
    if (!gt) return null;
    const puzzleType = String(gt.type ?? ro?.puzzleType ?? "").toUpperCase();
    if (puzzleType !== "WORD_SEARCH" && puzzleType !== "HIDDEN_MESSAGE") return null;
    const playId = getHomeBrowseGridPlayId(ro);
    if (!playId) return null;
    const title =
        typeof gt.title === "string" && gt.title.trim() ? gt.title.trim() : getHomeBrowsePuzzleTitle(ro);
    return {
        id: playId,
        type: puzzleType,
        title,
        difficultyLevel: gt.difficultyLevel ?? ro?.topic?.difficultyLevel ?? null,
        publishedAt: gt.publishedAt ?? null,
        free: gt.free === true,
        words: tplWordsInUseForCard(gt),
        template: {
            clusteredBubbles: gt.clusteredBubbles ?? null,
            gridRows: Number(gt.gridRows ?? gt.grid_rows) || 12,
            gridCols: Number(gt.gridCols ?? gt.grid_cols) || 12,
            /** Legacy size bucket: 12 = classic; 9 = Mini (any non-12×12). */
            gridSize: (() => {
                if (puzzleType !== "WORD_SEARCH") return 12;
                const r = Number(gt.gridRows ?? gt.grid_rows) || 12;
                const c = Number(gt.gridCols ?? gt.grid_cols) || 12;
                return r === 12 && c === 12 ? 12 : 9;
            })(),
            tags: gt.tags,
            topic: gt.topic,
        },
        genre: ro?.topic?.genre ?? null,
        topic: ro?.topic,
        state: gt.state,
        disabled: gt.state === "DISABLED" || gt.disabled === true,
    };
}

/**
 * @returns {{ puzzle: object, category: 'CROSSWORD'|'WORD_SEARCH'|'HIDDEN_MESSAGE' } | null}
 */
export function homeBrowseRowToCardPuzzle(ro) {
    const category = getBrowsePuzzleDisplayCategory(ro);
    if (category === "CROSSWORD") {
        const puzzle = homeBrowseRowToCrossCardPuzzle(ro);
        return puzzle ? { puzzle, category } : null;
    }
    if (category === "WORD_SEARCH" || category === "HIDDEN_MESSAGE") {
        const puzzle = homeBrowseRowToGridCardPuzzle(ro);
        return puzzle ? { puzzle, category } : null;
    }
    return null;
}

export function homePuzzleRoHasId(ro) {
    if (!ro || ro.id == null) return false;
    return String(ro.id).trim() !== "";
}

const BROWSE_GRID_PREVIEW_SIZE = 12;

function parseFilledCoordinatesRaw(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function parseHomeGridFilledCoordinates(ro) {
    const gt = homeGridTpl(ro);
    const raw =
        gt?.filledCoordinates
        ?? gt?.filled_coordinates
        ?? gt?.coordinates
        ?? ro?.filledCoordinates
        ?? ro?.filled_coordinates
        ?? ro?.coordinates
        ?? ro?.template?.filledCoordinates
        ?? ro?.template?.filled_coordinates
        ?? ro?.template?.coordinates;
    return parseFilledCoordinatesRaw(raw);
}

function parseHomeCrossFilledCoordinates(ro) {
    const ct = homeCrossTpl(ro);
    const raw =
        ct?.filledCoordinates
        ?? ct?.filled_coordinates
        ?? ct?.coordinates
        ?? ro?.filledCoordinates
        ?? ro?.filled_coordinates
        ?? ro?.coordinates
        ?? ro?.template?.filledCoordinates
        ?? ro?.template?.filled_coordinates
        ?? ro?.template?.coordinates;
    return parseFilledCoordinatesRaw(raw);
}

function gridFromFilledCoordinates(filledCoordinates, rows = BROWSE_GRID_PREVIEW_SIZE, cols = rows) {
    const r = Math.max(1, Number(rows) || BROWSE_GRID_PREVIEW_SIZE);
    const c = Math.max(1, Number(cols) || r);
    const grid = Array.from({ length: r }, () => Array(c).fill(" "));
    for (const coord of filledCoordinates) {
        for (const pos of coord.positions || []) {
            const row = pos.row;
            const col = pos.col;
            if (row >= 0 && row < r && col >= 0 && col < c) {
                const value =
                    pos.value != null && String(pos.value).trim() !== ""
                        ? String(pos.value).toUpperCase()
                        : " ";
                grid[row][col] = value;
            }
        }
    }
    return grid;
}

function resolveBrowseLetters(ro) {
    const raw =
        ro?.letters
        ?? ro?.crossword?.letters
        ?? ro?.grid?.letters
        ?? ro?.template?.letters;
    return typeof raw === "string" && raw.trim() ? raw : null;
}

function trimLetterLines(letters) {
    const lines = String(letters)
        .split("\n")
        .map((l) => l.replace(/\r$/, ""));
    while (lines.length > 0 && lines[lines.length - 1].trim() === "") {
        lines.pop();
    }
    return lines;
}

/** Build rows×cols uppercase cells from a letters matrix (pad/truncate to dims; no extra empty rows). */
function lettersToBrowseGrid(letters, rows, cols) {
    const r = Math.max(1, Number(rows) || BROWSE_GRID_PREVIEW_SIZE);
    const c = Math.max(1, Number(cols) || BROWSE_GRID_PREVIEW_SIZE);
    let lines = trimLetterLines(letters);
    const maxLine = Math.max(0, ...lines.map((l) => l.length));
    // e.g. 8×8 stored/joined as a 12-wide wrap (5×12+4) — reshape from the letter run.
    if (lines.length !== r || maxLine !== c) {
        const flat = lines.join("").replace(/[^a-zA-Z]/g, "");
        if (flat.length >= r * c) {
            lines = [];
            for (let i = 0; i < r; i++) {
                lines.push(flat.slice(i * c, (i + 1) * c));
            }
        }
    }
    const out = [];
    for (let i = 0; i < r; i++) {
        const line = lines[i] ?? "";
        const chars = line.split("").map((ch) => ch.toUpperCase());
        while (chars.length < c) chars.push(" ");
        out.push(chars.slice(0, c));
    }
    return out;
}

function applyFilledCoordinatesToGrid(grid, filledCoordinates) {
    if (!Array.isArray(grid) || !filledCoordinates?.length) return grid;
    for (const coordinate of filledCoordinates) {
        for (const pos of coordinate.positions || []) {
            if (
                pos.value != null
                && String(pos.value).trim() !== ""
                && grid[pos.row]?.[pos.col] !== undefined
            ) {
                grid[pos.row][pos.col] = String(pos.value).toUpperCase();
            }
        }
    }
    return grid;
}

function browseGridHasLetters(grid) {
    return (
        Array.isArray(grid)
        && grid.some((row) =>
            Array.isArray(row)
            && row.some((cell) => cell != null && String(cell).trim() !== "" && cell !== " "),
        )
    );
}

/** Prefer `letters` (library/play payloads); fall back to filledCoordinates-only grid. */
function buildBrowsePreviewGrid(ro, filledCoordinates, rows = BROWSE_GRID_PREVIEW_SIZE, cols = rows) {
    const letters = resolveBrowseLetters(ro);
    if (letters) {
        const grid = applyFilledCoordinatesToGrid(
            lettersToBrowseGrid(letters, rows, cols),
            filledCoordinates,
        );
        if (browseGridHasLetters(grid)) return grid;
    }
    const fromCoords = gridFromFilledCoordinates(filledCoordinates, rows, cols);
    return browseGridHasLetters(fromCoords) ? fromCoords : null;
}

function parseHomeCrossDisplayClue(ro) {
    const ct = homeCrossTpl(ro);
    const raw =
        ct?.displayClue
        ?? ct?.display_clue
        ?? ro?.crossword?.displayClue
        ?? ro?.crossword?.display_clue
        ?? ro?.displayClue
        ?? ro?.display_clue;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string" && raw.trim()) {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
}

function normalizeTeaserWord(word) {
    return String(word ?? "").trim().toLowerCase().replace(/-/g, "");
}

function buildCrossDisplayClueMap(displayClue) {
    const map = new Map();
    for (const entry of displayClue) {
        const word = entry?.word;
        const def = String(entry?.definition ?? "").trim();
        if (!word || !def) continue;
        const key = normalizeTeaserWord(word);
        if (key) map.set(key, def);
    }
    return map;
}

function truncateTeaserHint(text, maxLen = 44) {
    const s = String(text ?? "").trim();
    if (!s || s.length <= maxLen) return s;
    const cut = s.slice(0, maxLen).trim();
    const lastSpace = cut.lastIndexOf(" ");
    const base = lastSpace > maxLen * 0.55 ? cut.slice(0, lastSpace) : cut;
    return `${base}…`;
}

function splitCrossFilledByDirection(filledCoordinates) {
    const across = [];
    const down = [];
    for (const coord of filledCoordinates) {
        const word = String(coord?.text ?? coord?.word ?? "").trim();
        if (!word) continue;
        const entry = { word };
        if (coord.direction === "horizontal") {
            across.push(entry);
        } else if (coord.direction === "vertical") {
            down.push(entry);
        }
    }
    across.sort((a, b) => a.word.localeCompare(b.word));
    down.sort((a, b) => a.word.localeCompare(b.word));
    return { across, down };
}

/**
 * Partial across/down clue preview for landing cards — a few words with truncated definitions.
 * Returns null when no crossword words are available.
 */
export function buildCrosswordClueTeaserEntries(ro, { maxPerDirection = 2 } = {}) {
    const filledCoordinates = parseHomeCrossFilledCoordinates(ro);
    if (!filledCoordinates.length) return null;

    const defMap = buildCrossDisplayClueMap(parseHomeCrossDisplayClue(ro));
    const { across, down } = splitCrossFilledByDirection(filledCoordinates);

    const withHints = (items) =>
        items.slice(0, maxPerDirection).map(({ word }) => ({
            word,
            hint: truncateTeaserHint(defMap.get(normalizeTeaserWord(word)) ?? ""),
        }));

    const acrossEntries = withHints(across);
    const downEntries = withHints(down);
    if (!acrossEntries.length && !downEntries.length) return null;

    return { across: acrossEntries, down: downEntries };
}

/** 12×12 grid preview from GET /home/puzzle crossword rows (uses crossTpl.filledCoordinates). */
export function buildCrossBrowseGridPreviewFromRow(ro) {
    const ct = homeCrossTpl(ro);
    if (!ct) return null;
    const puzzleType = String(ct.type ?? ro?.type ?? ro?.puzzleType ?? "").toUpperCase();
    if (puzzleType !== "CROSSWORD") return null;
    const filledCoordinates = parseHomeCrossFilledCoordinates(ro);
    const grid = buildBrowsePreviewGrid(ro, filledCoordinates);
    if (!grid) return null;
    const words = filledCoordinates.length
        ? filledCoordinates
              .map((entry) => String(entry.text || entry.word || "").trim())
              .filter(Boolean)
        : [];
    return { grid, filledCoordinates, words };
}

/**
 * Browse card dims: prefer template gridRows×gridCols; else infer from letters.
 * HIDDEN_MESSAGE is always 12×12. Avoids padding Mini boards into a larger empty square.
 */
function resolveBrowseGridDims(ro, gt, puzzleType) {
    if (puzzleType !== "WORD_SEARCH") {
        return { rows: BROWSE_GRID_PREVIEW_SIZE, cols: BROWSE_GRID_PREVIEW_SIZE };
    }
    const metaR = Number(gt?.gridRows ?? gt?.grid_rows);
    const metaC = Number(gt?.gridCols ?? gt?.grid_cols);
    if (
        Number.isFinite(metaR) && metaR >= 5 && metaR <= 12
        && Number.isFinite(metaC) && metaC >= 5 && metaC <= 12
    ) {
        return { rows: metaR, cols: metaC };
    }
    const letters = resolveBrowseLetters(ro);
    if (letters) {
        const lines = trimLetterLines(letters);
        const r = lines.length;
        const c = Math.max(0, ...lines.map((l) => l.length));
        if (r >= 5 && r <= 12 && c >= 5 && c <= 12) {
            return { rows: r, cols: c };
        }
    }
    return { rows: BROWSE_GRID_PREVIEW_SIZE, cols: BROWSE_GRID_PREVIEW_SIZE };
}

/** Classic / Mini WORD_SEARCH (or 12×12 HIDDEN_MESSAGE) preview from GET /home/puzzle grid rows. */
export function buildGridBrowseGridPreviewFromRow(ro, expectedType) {
    const gt = homeGridTpl(ro);
    if (!gt) return null;
    const puzzleType = String(gt.type ?? ro?.type ?? ro?.puzzleType ?? "").toUpperCase();
    if (expectedType && puzzleType !== String(expectedType).toUpperCase()) return null;
    if (puzzleType !== "WORD_SEARCH" && puzzleType !== "HIDDEN_MESSAGE") return null;
    const filledCoordinates = parseHomeGridFilledCoordinates(ro);
    const { rows, cols } = resolveBrowseGridDims(ro, gt, puzzleType);
    const grid = buildBrowsePreviewGrid(ro, filledCoordinates, rows, cols);
    if (!grid) return null;
    return {
        grid,
        filledCoordinates,
        puzzleType,
        gridRows: rows,
        gridCols: cols,
        /** Longer edge — legacy callers that still expect a single size. */
        gridSize: Math.max(rows, cols),
    };
}
